// ============================================================
// BOSCO — Protection CSRF côté client (MOD-P4-B2)
//
// Récupère un token CSRF au chargement de la page et patche
// window.fetch pour l'injecter en en-tête `x-csrf-token` sur toutes
// les requêtes mutantes (POST/PUT/PATCH/DELETE) same-origin. Couvre
// automatiquement les ~30 appels fetch existants et toute route
// future, sans toucher au code métier.
//
// DOIT être le PREMIER script de chaque page (avant i18n.js et les
// scripts métier) pour patcher fetch avant tout appel :
//   - pages legacy : <script src="js/csrf.js"></script> (classique,
//     exécuté à l'analyse du <head>, avant les autres) ;
//   - login.html (ESM) : <script type="module" src="js/csrf.js">
//     placé en premier module (les modules s'exécutent dans l'ordre).
//
// Le token est servi par GET /api/csrf (cf. src/server/csrf.js).
// Le patch attend le token avant d'envoyer une mutation, donc même
// une mutation déclenchée très tôt part avec l'en-tête.
//
// Reprise automatique : sur un refus CSRF (403 + code CSRF_INVALIDE),
// le token est redemandé et la mutation rejouée une fois. Couvre un
// onglet mobile réveillé après une veille, dont le token a expiré ou
// dont le navigateur a effacé le cookie (incident du 2026-09-13 : jets
// de dés refusés en silence jusqu'au rechargement de la page).
// ============================================================

(() => {
    'use strict';

    const fetchOriginal = window.fetch.bind(window);
    const METHODES_MUTANTES = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
    let promesseToken = null;

    // Récupère le token (mémoïsé). Utilise fetchOriginal pour ne pas
    // repasser par le wrapper (GET de toute façon ignoré, mais évite
    // toute interaction). Réinitialise la promesse en cas d'échec pour
    // permettre une nouvelle tentative à la prochaine mutation.
    function recupererToken() {
        if (!promesseToken) {
            // cache: 'no-store' : au réveil d'un onglet mobile, le navigateur
            // pouvait resservir un token périmé depuis son cache (incident
            // du 2026-09-13). Le serveur pose aussi Cache-Control: no-store.
            promesseToken = fetchOriginal('/api/csrf', { credentials: 'same-origin', cache: 'no-store' })
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => (d ? d.csrfToken : null))
                .catch(() => {
                    promesseToken = null;
                    return null;
                });
        }
        return promesseToken;
    }

    function estSameOrigin(input) {
        try {
            const cible = typeof input === 'string' ? input : input.url;
            return new URL(cible, window.location.origin).origin === window.location.origin;
        } catch {
            // URL relative non résolue → considérée same-origin (cas Bosco).
            return true;
        }
    }

    function methodeDe(input, init) {
        const m = init.method || (typeof input !== 'string' && input && input.method) || 'GET';
        return String(m).toUpperCase();
    }

    function envoyerAvecToken(input, init, token) {
        const headers = new Headers(init.headers || (typeof input !== 'string' && input && input.headers) || {});
        if (token) headers.set('x-csrf-token', token);
        return fetchOriginal(input, { ...init, headers });
    }

    // Vrai si la réponse est un refus CSRF (code dédié posé par
    // gestionErreurCsrf côté serveur), à distinguer d'un 403
    // d'autorisation. Lit un clone : le corps reste lisible par l'appelant.
    async function estRefusCsrf(reponse) {
        if (reponse.status !== 403) return false;
        try {
            const corps = await reponse.clone().json();
            return corps?.code === 'CSRF_INVALIDE';
        } catch {
            return false;
        }
    }

    window.fetch = (input, init = {}) => {
        const methode = methodeDe(input, init);
        if (!METHODES_MUTANTES.has(methode) || !estSameOrigin(input)) {
            return fetchOriginal(input, init);
        }
        // Un en-tête posé explicitement par l'appelant est respecté tel quel.
        const enTetesAppelant = new Headers(init.headers || {});
        if (enTetesAppelant.has('x-csrf-token')) return fetchOriginal(input, init);

        return recupererToken().then(async (token) => {
            const reponse = await envoyerAvecToken(input, init, token);
            if (!(await estRefusCsrf(reponse))) return reponse;
            // Token périmé (onglet réveillé après une veille, cookie effacé
            // par le navigateur…) : on en redemande un et on rejoue UNE fois.
            // Sans risque de double écriture : le serveur refuse avant
            // d'atteindre la route métier. Un corps de type flux (Request
            // avec body stream) ne serait pas rejouable, mais Bosco n'envoie
            // que des chaînes JSON.
            promesseToken = null;
            const tokenFrais = await recupererToken();
            if (!tokenFrais || tokenFrais === token) return reponse;
            return envoyerAvecToken(input, init, tokenFrais);
        });
    };

    // Pré-charge le token dès le boot (réduit la latence de la 1re mutation).
    recupererToken();
})();
